import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { rAFTimeout } from '../../../utils/utils';

import * as styles from './LocationSelector.module.scss';

import closeIcon from '../svg/close.svg';

interface LocationSelectorProps {
  open: boolean;
  cities: Array<{ name: string; id: number }>;
  currentCityId: number;
  onCitySelect: (cityId: number) => void;
  onClose: () => void;
}

const LocationSelector = ({
  open,
  cities,
  currentCityId,
  onCitySelect,
  onClose,
}: LocationSelectorProps): React.ReactElement => {
  const { t } = useTranslation();
  const transitionRef = useRef<HTMLDivElement>(null);
  const selectorContainerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const selectorRef = useRef<HTMLUListElement>(null);
  const closeButtonRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (open) {
      rAFTimeout(
        () => transitionRef.current && transitionRef.current.classList.add(styles.animateIn),
        1,
      );
      rAFTimeout(
        () =>
          selectorContainerRef.current && selectorContainerRef.current.classList.add(styles.open),
        50,
      );
      rAFTimeout(() => headerRef.current && headerRef.current.classList.add(styles.animateIn), 100);
      rAFTimeout(
        () => closeButtonRef.current && closeButtonRef.current.classList.add(styles.animateIn),
        100,
      );
      rAFTimeout(
        () => selectorRef.current && selectorRef.current.classList.add(styles.animateIn),
        150,
      );
    } else {
      rAFTimeout(
        () => selectorRef.current && selectorRef.current.classList.remove(styles.animateIn),
        1,
      );
      rAFTimeout(
        () => closeButtonRef.current && closeButtonRef.current.classList.remove(styles.animateIn),
        1,
      );
      rAFTimeout(
        () => headerRef.current && headerRef.current.classList.remove(styles.animateIn),
        1,
      );
      rAFTimeout(
        () => transitionRef.current && transitionRef.current.classList.remove(styles.animateIn),
        100,
      );
      rAFTimeout(
        () =>
          selectorContainerRef.current &&
          selectorContainerRef.current.classList.remove(styles.open),
        150,
      );
    }
  }, [open]);

  return (
    <>
      <div ref={transitionRef} className={styles.transition}></div>
      <div ref={selectorContainerRef} className={styles.selectorContainer}>
        <div ref={headerRef} className={styles.header}>
          {t('weather_widget.pick_a_city_label', 'Pick a city')}
        </div>
        <img
          ref={closeButtonRef}
          className={styles.closeButton}
          onClick={onClose}
          src={closeIcon}
          alt="closeButton"
        ></img>
        <ul ref={selectorRef} className={styles.selector}>
          {cities.map(city => (
            <li
              className={
                styles.cityEntry + (city.id === currentCityId ? ' ' + styles.selected : '')
              }
              key={city.id}
              onClick={() => {
                onCitySelect(city.id);
              }}
            >
              {city.name}
            </li>
          ))}
        </ul>
      </div>
    </>
  );
};

export default LocationSelector;
